<template>
  <section class="testimonials-section">
    <div class="container">
      <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
        <h2 class="section-title">Lo que dicen de nosotros</h2>
        <p class="section-subtitle">
          Conoce las experiencias de familias que ya forman parte de nuestra comunidad.
        </p>
      </div>
      
      <div class="row">
        <div
          v-for="(_, cardIndex) in 3"
          :key="`card-${cardIndex}`"
          class="col-lg-4 col-md-6 mb-4"
        >
          <div class="testimonial-card animate-on-scroll fade-in-up" :style="`animation-delay: ${cardIndex * 0.1}s`">
            <transition name="testimonial-fade" mode="out-in">
              <div :key="currentIndexes[cardIndex]" class="testimonial-content">
                <p class="testimonial-text">{{ displayedTestimonials[cardIndex].text }}</p>
              </div>
            </transition>
            <div class="testimonial-author">
              <div class="author-avatar">
                <i class="fas fa-map-marker-alt"></i>
              </div>
              <div class="author-info">
                <h5 class="author-name">{{ displayedTestimonials[cardIndex].clinicName }}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * Sección de testimonios con rotación automática.
 * Muestra nombres de clínicas del área de servicio en lugar de autores.
 */
export default {
  name: 'TestimonialsSection',
  props: {
    testimonials: {
      type: Array,
      required: true,
      default: () => []
    },
    locations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentIndexes: [0, 1, 2],
      rotationInterval: null,
      ROTATION_MS: 5000
    };
  },
  computed: {
    enrichedTestimonials() {
      const locs = this.locations && this.locations.length > 0 ? this.locations : [];
      return this.testimonials.map((t, i) => ({
        ...t,
        clinicName: locs[i % locs.length]?.name || t.author || 'Nuestra comunidad'
      }));
    },
    displayedTestimonials() {
      const list = this.enrichedTestimonials;
      if (list.length === 0) return [{ text: '', clinicName: '' }, { text: '', clinicName: '' }, { text: '', clinicName: '' }];
      return [
        list[this.currentIndexes[0] % list.length] || list[0],
        list[this.currentIndexes[1] % list.length] || list[1] || list[0],
        list[this.currentIndexes[2] % list.length] || list[2] || list[0]
      ];
    }
  },
  mounted() {
    this.startRotation();
  },
  beforeDestroy() {
    this.stopRotation();
  },
  methods: {
    startRotation() {
      this.rotationInterval = setInterval(() => {
        const len = this.enrichedTestimonials.length;
        if (len === 0) return;
        this.currentIndexes = [
          (this.currentIndexes[0] + 1) % len,
          (this.currentIndexes[1] + 1) % len,
          (this.currentIndexes[2] + 1) % len
        ];
      }, this.ROTATION_MS);
    },
    stopRotation() {
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
        this.rotationInterval = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.testimonials-section {
  padding: 100px 0;
  position: relative;
  background: linear-gradient(to bottom right, $landing-bg-white 0%, $landing-bg-white 75%, #1a1a1a 100%);
  overflow: hidden;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 300px;
    pointer-events: none;
    z-index: 0;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, $landing-primary-rgba-40 0%, transparent 100%);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, $landing-primary-rgba-40 0%, transparent 100%);
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  .section-header {
    margin-bottom: 4rem;
    
    .section-title {
      color: $landing-primary-color;
      font-size: 3.5rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
    }
    
    .section-subtitle {
      color: $landing-primary-color;
      font-size: 2rem;
      font-weight: 500;
      line-height: 1.6;
    }
  }
  
  .testimonial-card {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    box-shadow: 0 5px 20px $landing-primary-rgba-08;
    transition: all 0.3s ease;
    border: 2px solid $landing-border-light;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 30px $landing-primary-rgba-25;
      border-color: $landing-primary-color;
    }
    
    .testimonial-content {
      flex: 1;
      margin-bottom: 1.5rem;
      min-height: 120px;
      
      .testimonial-text {
        font-size: 1.4rem;
        color: $landing-primary-color;
        line-height: 1.8;
        font-style: italic;
        margin: 0;
        font-weight: 500;
      }
    }
    
    .testimonial-author {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding-top: 1rem;
      border-top: 2px solid $landing-bg-gray;
      
      .author-avatar {
        width: 50px;
        height: 50px;
        background: $landing-primary-gradient;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        
        i {
          font-size: 1.5rem;
          color: white;
        }
      }
      
      .author-info {
        .author-name {
          font-size: 1.4rem;
          font-weight: 700;
          color: $landing-primary-color;
          margin: 0;
        }
      }
    }
  }
  
  @media (max-width: 991px) {
    padding: 80px 0;
    
    .section-header {
      .section-title {
        font-size: 2.5rem;
      }
      
      .section-subtitle {
        font-size: 1.3rem;
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 60px 0;
    
    .section-header {
      margin-bottom: 3rem;
      
      .section-title {
        font-size: 2rem;
      }
      
      .section-subtitle {
        font-size: 1.1rem;
      }
    }
    
    .testimonial-card {
      padding: 1.5rem;
      
      .testimonial-content .testimonial-text {
        font-size: 1.1rem;
      }
      
      .testimonial-author .author-info .author-name {
        font-size: 1.2rem;
      }
    }
  }
}

.testimonial-fade-enter-active,
.testimonial-fade-leave-active {
  transition: opacity 0.4s ease;
}
.testimonial-fade-enter,
.testimonial-fade-leave-to {
  opacity: 0;
}
</style>
